<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@chatwoot/pico-search';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});
const searchQuery = ref('');
const labelsGuideDialogRef = ref(null);

const records = computed(() => getters['labels/getLabels'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'title', weight: 4 },
    'description',
  ]);
});
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const openLabelsGuide = () => labelsGuideDialogRef.value?.open();
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

const guideCategories = computed(() => [
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.STATUS'),
    examples: ['nuevo', 'en-proceso', 'cerrado'],
  },
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.SALES_STAGE'),
    examples: ['interesado', 'cotizacion', 'negociacion', 'venta'],
  },
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.AUTOMATION'),
    examples: ['atendiendo_bot', 'atendiendo_humano'],
  },
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.PRIORITY'),
    examples: ['urgente', 'vip'],
  },
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.ORIGIN'),
    examples: ['facebook', 'instagram', 'whatsapp', 'web'],
    wide: true,
  },
]);

const tableHeaders = computed(() => {
  return [
    t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.ACTION'),
  ];
});

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LABEL_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('LABEL_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('LABEL_MGMT.HEADER')"
        :description="$t('LABEL_MGMT.DESCRIPTION')"
        :link-text="$t('LABEL_MGMT.LEARN_MORE')"
        :search-placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="labels"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('LABEL_MGMT.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <div class="flex items-center gap-2">
            <Button
              v-tooltip.top="$t('LABEL_MGMT.GUIDE.BUTTON_TOOLTIP')"
              icon="i-lucide-info"
              color="slate"
              variant="ghost"
              size="sm"
              @click="openLabelsGuide"
            />
            <Button
              :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
              size="sm"
              @click="openAddPopup"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery ? $t('LABEL_MGMT.NO_RESULTS') : $t('LABEL_MGMT.LIST.404')
        "
      >
        <template #row="{ items }">
          <BaseTableRow v-for="label in items" :key="label.title" :item="label">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12">
                  {{ label.title }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ label.description }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <div class="flex items-center">
                  <span
                    class="w-4 h-4 ltr:mr-2 rtl:ml-2 border border-solid rounded border-n-weak"
                    :style="{ backgroundColor: label.color }"
                  />
                  <span class="text-body-main text-n-slate-12">
                    {{ label.color }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    :is-loading="loading[label.id]"
                    @click="openEditPopup(label)"
                  />
                  <Button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[label.id]"
                    @click="openDeletePopup(label)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <Dialog
      ref="labelsGuideDialogRef"
      width="2xl"
      :title="$t('LABEL_MGMT.GUIDE.TITLE')"
      :confirm-button-label="$t('LABEL_MGMT.GUIDE.CLOSE')"
      :show-cancel-button="false"
      @confirm="labelsGuideDialogRef.close()"
    >
      <div class="flex flex-col gap-5">
        <div
          class="flex gap-3 p-4 border rounded-xl border-n-blue-5 bg-n-blue-3"
        >
          <span class="flex-shrink-0 i-lucide-tags size-6 text-n-blue-10" />
          <p class="text-sm leading-6 text-n-blue-12">
            {{ $t('LABEL_MGMT.GUIDE.DESCRIPTION') }}
          </p>
        </div>

        <div>
          <h4 class="mb-3 text-sm font-semibold text-n-slate-12">
            {{ $t('LABEL_MGMT.GUIDE.RECOMMENDATION') }}
          </h4>
          <div class="grid gap-3 sm:grid-cols-2">
            <div
              v-for="category in guideCategories"
              :key="category.title"
              class="p-3 border rounded-xl border-n-weak bg-n-slate-2"
              :class="{ 'sm:col-span-2': category.wide }"
            >
              <p class="mb-2 text-sm font-medium text-n-slate-12">
                {{ category.title }}
              </p>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-for="example in category.examples"
                  :key="example"
                  class="label-guide-chip"
                >
                  {{ example }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>

<style scoped>
.label-guide-chip {
  @apply inline-flex items-center rounded-md border border-n-weak bg-n-slate-1 px-2 py-1 font-mono text-xs text-n-slate-11;
}
</style>
